import { useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import moment from "moment";
import OpsPanel from "../OpsPanel";
import { Token } from "../../theme";
import Timeoff from "../../models/Timeoff";
import OpsDateUtils from "../../utils/OpsDateUtils";
import { buildCoverageRows, buildTwoWeekRange, CoverageState, resolveCellState } from "../../utils/TeamCoverageUtils";

interface TeamCoverageTileProps {
  rows: Timeoff[];
}

const CELL_STYLES: Record<CoverageState, { background: string; border: string }> = {
  working: { background: Token.actionPrimary, border: "transparent" },
  timeoff: { background: Token.timeoffBg, border: Token.magenta },
  weekend: { background: Token.surfaceSunken, border: "transparent" },
};

const LEGEND: { state: CoverageState; label: string }[] = [
  { state: "working", label: "Working" },
  { state: "timeoff", label: "Time off" },
  { state: "weekend", label: "Weekend" },
];

const NAME_COLUMN_WIDTH = 150;

export default function TeamCoverageTile({ rows }: TeamCoverageTileProps) {
  const range = useMemo(() => buildTwoWeekRange(), []);
  const coverageRows = useMemo(() => buildCoverageRows(rows, range), [rows, range]);

  if (coverageRows.length === 0) {
    return (
      <OpsPanel title="Team coverage · next two weeks">
        <Box style={{ display: "flex", flexDirection: "column", gap: 6, padding: "16px 0", textAlign: "center" }}>
          <span style={{ fontSize: 13, color: Token.textSecondary }}>Full coverage</span>
          <span style={{ fontSize: 11.5, color: Token.textTertiary }}>No time off scheduled for the next two weeks</span>
        </Box>
      </OpsPanel>
    );
  }
  const gridColumns = `${NAME_COLUMN_WIDTH}px repeat(${range.length}, minmax(0, 1fr))`;

  return (
    <OpsPanel
      title="Team coverage · next two weeks"
      panelMenu={
        <span style={{ fontSize: 12, color: Token.textTertiary }}>
          {coverageRows.length} {coverageRows.length === 1 ? "person" : "people"} away
        </span>
      }
    >
      <Box style={{ display: "flex", flexDirection: "column", gap: 6, height: "100%" }}>
        <Box style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 3, alignItems: "center" }}>
          <span />
          {range.map((day) => (
            <span
              key={day.getTime()}
              style={{
                fontSize: 9,
                textAlign: "center",
                color: OpsDateUtils.isWeekend(day) ? Token.textTertiary : Token.textSecondary,
              }}
            >
              {moment(day).format("dd").charAt(0)}
            </span>
          ))}
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxHeight: 180,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          {coverageRows.map((row) => (
            <Box key={row.requesterExternalId} style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 3, alignItems: "center" }}>
              <span
                style={{
                  fontSize: 12,
                  color: Token.textPrimary,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.fullName}
              </span>
              {range.map((day) => {
                const state = resolveCellState(day, row.days);
                const cell = (
                  <Box
                    style={{
                      height: 16,
                      borderRadius: 3,
                      backgroundColor: CELL_STYLES[state].background,
                      border: `1px solid ${CELL_STYLES[state].border}`,
                      boxSizing: "border-box",
                    }}
                  />
                );
                return state === "timeoff" ? (
                  <Tooltip key={day.getTime()} title={`${row.fullName} · ${moment(day).format("ddd D MMM")}`}>
                    {cell}
                  </Tooltip>
                ) : (
                  <Box key={day.getTime()}>{cell}</Box>
                );
              })}
            </Box>
          ))}
        </Box>
        <Box
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            marginTop: "auto",
            paddingTop: 10,
            borderTop: `1px solid ${Token.borderDefault}`,
          }}
        >
          {LEGEND.map(({ state, label }) => (
            <Box key={state} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  backgroundColor: CELL_STYLES[state].background,
                  border: `1px solid ${CELL_STYLES[state].border}`,
                  boxSizing: "border-box",
                }}
              />
              <span style={{ fontSize: 11, color: Token.textTertiary }}>{label}</span>
            </Box>
          ))}
        </Box>
      </Box>
    </OpsPanel>
  );
}
